/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { JsEventsServerConfig } from '../config/js-events.config';
import {
    DataCollectionConsent,
    JsEventsCommonData,
    ConsentLevel,
    UnassignedStates,
    UserConsentInfo,
    ExperimentActivationType
} from '../models/common-data.models';
import { TelemetryEvent } from '../telemetry/telemetry.models';

export const JsEventsDefaultConfig: JsEventsServerConfig = {
    server: 'https://events.telemetry.data.nvidia.com',
    version: 'v1.0',
    schemaVersion: '1.0',
    msBetweenSendRequest: 5000,
    maxEventsPerRequest: 128,
    clientId: '',
    systemInfoEventDelay: 10000,
    productName: 'GFN',
    retryConfig: {
        defaultRetries: 2,
        defaultTimeout: 30000,
        defaultTimeBetweenRetries: 1000
    }
};

const sampleEvent = new TelemetryEvent<any>({});

export const JsEventsDefaultCommonData: JsEventsCommonData = {
    clientId: '{CLIENTID}',
    clientVer: '{CLIENTVER}',
    eventSchemaVer: '{EVENTSCHEMAVER}',
    eventSysVer: '0.18.1',
    deviceId: UnassignedStates.Undefined,
    userId: UnassignedStates.Undefined,
    externalUserId: UnassignedStates.Undefined,
    idpId: UnassignedStates.Undefined,
    sessionId: UnassignedStates.Undefined,
    deviceOS: UnassignedStates.Undefined,
    deviceOSVersion: UnassignedStates.Undefined,
    deviceType: UnassignedStates.Undefined,
    deviceModel: UnassignedStates.Undefined,
    deviceMake: UnassignedStates.Undefined,
    clientType: UnassignedStates.Undefined,
    clientVariant: UnassignedStates.Undefined,
    sentTs: '',
    events: [sampleEvent],
    integrationId: UnassignedStates.Undefined,
    browserType: UnassignedStates.Undefined,
    productName: UnassignedStates.Undefined,
    productVersion: UnassignedStates.Undefined,
    cpuArchitecture: UnassignedStates.Undefined
};

export const GdprConsentNone: Readonly<DataCollectionConsent> = {
    required: ConsentLevel.None,
    functional: ConsentLevel.None,
    technical: ConsentLevel.None,
    behavioral: ConsentLevel.None
};

export const GdprConsentTemporary: Readonly<DataCollectionConsent> = {
    required: ConsentLevel.Temp,
    functional: ConsentLevel.Temp,
    technical: ConsentLevel.None,
    behavioral: ConsentLevel.None
};

export const GdprConsentRequired: Readonly<DataCollectionConsent> = {
    required: ConsentLevel.Full,
    functional: ConsentLevel.None,
    technical: ConsentLevel.None,
    behavioral: ConsentLevel.None
};

export const GdprConsentFunctional: Readonly<DataCollectionConsent> = {
    required: ConsentLevel.Full,
    functional: ConsentLevel.Full,
    technical: ConsentLevel.None,
    behavioral: ConsentLevel.None
};

export const GdprConsentTechnical: Readonly<DataCollectionConsent> = {
    required: ConsentLevel.Full,
    functional: ConsentLevel.Full,
    technical: ConsentLevel.Full,
    behavioral: ConsentLevel.None
};

export enum EventsReturnCode {
    Ok = 'OK',
    InvalidInfoForEventType = 'INVALID_INFO_FOR_EVENT_TYPE',
    UnknownEventType = 'UNKNOWN_EVENT_TYPE',
    UnProcessed = 'UNPROCESSED',
    ExperimentAlreadyActive = 'EXPERIMENT_ALREADY_ACTIVE',
    FunctionalConsentNotReceived = 'FUNCTIONAL_CONSENT_NOT_RECEIVED',
    LocaleSwitchInProgress = 'LOCALE_SWITCH_INPROGRESS'
}

export enum PlatformType {
    Win = 'Win',
    Mac = 'Mac',
    Android = 'Android',
    NvAndroid = 'NvAndroid',
    Linux = 'Linux',
    Web = 'Web',
    Undefined = 'undefined'
}

export const DefaultConsentBlock: Readonly<UserConsentInfo> = {
    userConsent: GdprConsentNone,
    userId: '',
    externalUserId: '',
    idpId: ''
};

export const DefaultExperienceControlExperimentContext = {
    id: '',
    cvName: '',
    group: '',
    name: '',
    activationType: ExperimentActivationType.DEVICE_ID
};

export const TelemetryStringLengthLimit = 1000;
